/**
 * Compound controls for dynamic-block parameters whose value is a pair
 * of numbers (polar / xy / point / basePoint). Complements the scalar
 * controls in `dynamicBlockControls.tsx`.
 *
 * AutoCAD's Properties palette renders each polar / xy / point
 * parameter as ONE row with two labelled sub-fields — the driver UX is
 * a compound widget, not two independent scalar rows. These controls
 * match that behaviour: two number inputs side-by-side, a single shared
 * draft buffer so Escape reverts BOTH fields atomically, blur/Enter
 * commits the combined `"a,b"` string via `onCommit`.
 *
 * ── Encoding ──
 *
 * `value` and the `onCommit` payload are comma-separated pairs:
 *
 *   polar   → "<distance>,<angle>"    (distance mm, angle °)
 *   xy      → "<x>,<y>"                (both in mm)
 *   point   → "<x>,<y>"                (drawing units, no suffix)
 *   basePt  → "<x>,<y>"                (drawing units, no suffix)
 *
 * Chosen because:
 *   - `dynamic/set` accepts any rawValue string (no schema change) — the
 *     caller (InspectorSidebar) splits the pair back into two
 *     dispatches, one per `<name>.x` / `<name>.y` / `<name>.distance` /
 *     `<name>.angle` suffix consumed by `applyInstanceParameters` in
 *     drawing-model/dynamicBlockMetadata.ts (see the customProperties
 *     key table at the top of that file).
 *   - Minimal reversible format for two numbers — no quoting / escaping
 *     required because numbers never contain commas.
 *   - Mirrors how AutoCAD's command line prints polar coordinates
 *     (`@100,45` or `100,45`).
 *
 * The compound kinds themselves (`polar | xy | point | basePoint`) are
 * a UI-only widening of `DynamicParamType`; the CLI (DYNLIST / DYNSET /
 * DYNRESET) still operates on scalar rows. InspectorSidebar groups
 * suffix-keyed DynListLine rows into compound descriptors before
 * handing them to this widget — see `collectRenderRows` in
 * `dynamicBlockCompoundGrouping.ts`.
 */

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from "react";

import type { DynControlProps } from "./dynamicBlockControlTypes";

// ═══════════════════════════════════════════════════════════════════════
// Pair encode / decode helpers (exported for tests + InspectorSidebar)
// ═══════════════════════════════════════════════════════════════════════

interface ParsedPair {
  a: string;
  b: string;
}

/**
 * Split a comma-separated `"a,b"` pair. Non-string inputs are coerced
 * via `String(raw)` — descriptor values frequently arrive as numbers
 * or pre-formatted strings.
 *
 * Empty / missing half returns as empty string rather than undefined —
 * keeps the draft-state initializer happy (inputs render as `""`,
 * which is valid for `<input type="number" />`).
 */
function splitPair(raw: string | number | boolean): ParsedPair {
  const str = String(raw);
  const idx = str.indexOf(",");
  if (idx < 0) return { a: str, b: "" };
  return { a: str.slice(0, idx).trim(), b: str.slice(idx + 1).trim() };
}

/** Join two already-stringified values into the `"a,b"` format. */
function joinPair(a: string, b: string): string {
  return `${a},${b}`;
}

// ═══════════════════════════════════════════════════════════════════════
// Shared compound implementation
// ═══════════════════════════════════════════════════════════════════════

interface CompoundControlProps extends DynControlProps {
  /** Display label for the "a" sub-field (e.g. distance / X). */
  labelA: string;
  /** Display label for the "b" sub-field (e.g. angle / Y). */
  labelB: string;
  /** Unit suffix for the "a" sub-field (mm / empty). */
  unitA?: string;
  /** Unit suffix for the "b" sub-field (° / mm / empty). */
  unitB?: string;
  /** Aria-label override for the "a" input; defaults to `labelA`. */
  ariaA?: string;
  /** Aria-label override for the "b" input; defaults to `labelB`. */
  ariaB?: string;
  /** testid suffix for the whole compound (e.g. "polar", "xy", "point"). */
  testIdSuffix: string;
}

/**
 * Generic two-field compound input. Shares a single draft buffer so
 * Escape on either input reverts BOTH fields atomically (mirrors
 * AutoCAD's Properties palette: escaping a polar edit cancels both
 * distance and angle, not just the focused field). Blur on either or
 * Enter commits the combined `"a,b"` string via `onCommit`.
 */
function CompoundPairControl(props: CompoundControlProps): ReactElement {
  const initial = splitPair(props.param.value);
  const [draftA, setDraftA] = useState(initial.a);
  const [draftB, setDraftB] = useState(initial.b);
  const lastCommittedRef = useRef<ParsedPair>(initial);

  // Re-sync on external value changes (reducer echoes back after our
  // dispatch, or selection switches to a different parameter). Comparing
  // against `lastCommittedRef` avoids clobbering the draft mid-edit —
  // only the first render after a confirmed external change resets the
  // fields.
  useEffect(() => {
    const next = splitPair(props.param.value);
    const last = lastCommittedRef.current;
    if (next.a !== last.a || next.b !== last.b) {
      lastCommittedRef.current = next;
      setDraftA(next.a);
      setDraftB(next.b);
    }
  }, [props.param.value]);

  function commit(): void {
    const last = lastCommittedRef.current;
    if (draftA === last.a && draftB === last.b) return;
    lastCommittedRef.current = { a: draftA, b: draftB };
    props.onCommit(joinPair(draftA, draftB));
  }

  function revert(): void {
    const last = lastCommittedRef.current;
    setDraftA(last.a);
    setDraftB(last.b);
  }

  function handleKey(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
      (event.target as HTMLInputElement).blur();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      revert();
      (event.target as HTMLInputElement).blur();
    }
  }

  const name = props.param.name;
  const suffix = props.testIdSuffix;

  function renderField(
    slot: "a" | "b",
    draft: string,
    setDraft: (v: string) => void,
    label: string,
    aria: string,
    unit: string | undefined,
  ): ReactElement {
    const input = (
      <input
        type="number"
        step="any"
        className="property-input"
        value={draft}
        aria-label={aria}
        data-testid={`dyn-param-${name}-${suffix}-${slot}`}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
      />
    );
    return (
      <span className="dyn-param-input-group" key={slot}>
        <span
          className="dyn-param-axis-label"
          data-testid={`dyn-param-${name}-${suffix}-${slot}-label`}
        >
          {label}
        </span>
        {input}
        {unit ? (
          <span
            className="dyn-param-unit"
            data-testid={`dyn-param-${name}-${suffix}-${slot}-unit`}
          >
            {unit}
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <span
      className="dyn-param-compound"
      data-testid={`dyn-param-${name}-${suffix}`}
    >
      {renderField(
        "a",
        draftA,
        setDraftA,
        props.labelA,
        props.ariaA ?? `${props.editAriaLabel} — ${props.labelA}`,
        props.unitA,
      )}
      {renderField(
        "b",
        draftB,
        setDraftB,
        props.labelB,
        props.ariaB ?? `${props.editAriaLabel} — ${props.labelB}`,
        props.unitB,
      )}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Per-kind thin wrappers
// ═══════════════════════════════════════════════════════════════════════

/**
 * Polar parameter widget — (distance mm, angle °). Matches the AutoCAD
 * Polar Parameter's Properties-palette shape. Uses the same unit labels
 * as the individual distance / angle scalar controls.
 */
export function DynPolarControl(props: DynControlProps): ReactElement {
  const compoundProps: CompoundControlProps = {
    ...props,
    labelA: "Д",
    labelB: "А",
    ...(props.unitMmLabel !== undefined ? { unitA: props.unitMmLabel } : {}),
    ...(props.unitDegLabel !== undefined ? { unitB: props.unitDegLabel } : {}),
    ...(props.distanceAriaLabel !== undefined
      ? { ariaA: props.distanceAriaLabel }
      : {}),
    ...(props.angleAriaLabel !== undefined
      ? { ariaB: props.angleAriaLabel }
      : {}),
    testIdSuffix: "polar",
  };
  return <CompoundPairControl {...compoundProps} />;
}

/**
 * XY parameter widget — (x mm, y mm). AutoCAD labels the Properties
 * fields "Distance X" / "Distance Y"; we use shorter axis letters to
 * fit the Inspector's narrower column layout.
 */
export function DynXYControl(props: DynControlProps): ReactElement {
  const compoundProps: CompoundControlProps = {
    ...props,
    labelA: props.axisXLabel ?? "X",
    labelB: props.axisYLabel ?? "Y",
    ...(props.unitMmLabel !== undefined
      ? { unitA: props.unitMmLabel, unitB: props.unitMmLabel }
      : {}),
    ...(props.xAriaLabel !== undefined ? { ariaA: props.xAriaLabel } : {}),
    ...(props.yAriaLabel !== undefined ? { ariaB: props.yAriaLabel } : {}),
    testIdSuffix: "xy",
  };
  return <CompoundPairControl {...compoundProps} />;
}

/**
 * Point / basePoint parameter widget — (x, y) drawing-unit pair with
 * NO unit suffix. AutoCAD treats Point parameter fields as raw
 * drawing-unit values; the effective unit is read from the drawing's
 * INSUNITS / dimension style, not the Properties row itself.
 */
export function DynPointControl(props: DynControlProps): ReactElement {
  const compoundProps: CompoundControlProps = {
    ...props,
    labelA: props.axisXLabel ?? "X",
    labelB: props.axisYLabel ?? "Y",
    ...(props.xAriaLabel !== undefined ? { ariaA: props.xAriaLabel } : {}),
    ...(props.yAriaLabel !== undefined ? { ariaB: props.yAriaLabel } : {}),
    testIdSuffix: "point",
  };
  return <CompoundPairControl {...compoundProps} />;
}
